import clsx from 'clsx';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useState } from 'react';
import { Copy, Plus, X } from 'react-bootstrap-icons';

import { minutesToTimeString, TIME, timeStringToMinutes } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSIconButton } from '@repo/sds/SDSIconButton';
import { SDSText } from '@repo/sds/SDSText';
import { ConfirmationModal } from '@repo/ui/components/elements/ConfirmationModal/ConfirmationModal';
import { TimeSelector } from '@repo/ui/components/elements/TimeSelector/TimeSelector';

import { useAvailability } from '../../hooks/use-availability';
import type { TimeRange } from '../../types/time';
import { CopyDaysPopover } from '../CopyDaysPopover/CopyDaysPopover';

import './DayTimeRangeSelector.scss';

interface DayTimeRangeSelectorProps {
  timeRange: TimeRange;
  day: string;
  index: number;
  disabled?: boolean;
}

function getTimeRangeError(timeRange: TimeRange, overlaps: boolean): string | null {
  if (timeRange.start >= timeRange.end) return 'End time must be after start time';
  return overlaps ? 'Time range overlaps. Adjust start or end time.' : null;
}

function getMinimumEndTime(startTime: string): string | undefined {
  const minimumEndMinutes = timeStringToMinutes(startTime) + 15;
  return minimumEndMinutes < TIME.MINUTES.PER_DAY ? minutesToTimeString(minimumEndMinutes) : undefined;
}

function getActionColor(disabled: boolean): 'muted' | 'primary' {
  return disabled ? 'muted' : 'primary';
}

function TimeRangeActions({
  day,
  disabled,
  index,
  timeRange,
  onAdd,
  onCopy,
  onRemove,
}: {
  day: string;
  disabled: boolean;
  index: number;
  timeRange: TimeRange;
  onAdd: () => void;
  onCopy: (event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => void;
  onRemove: () => void;
}) {
  const color = getActionColor(disabled);
  if (index > 0) {
    return (
      <SDSIconButton
        size="medium"
        color={color}
        icon={<X />}
        onClick={onRemove}
        isDisabled={disabled}
        ariaLabel={`Remove time range from ${timeRange.start} to ${timeRange.end}`}
      />
    );
  }

  return (
    <>
      <SDSIconButton
        size="medium"
        color={color}
        icon={<Plus />}
        onClick={onAdd}
        isDisabled={disabled}
        ariaLabel={`Add time range for ${day}`}
      />
      <SDSIconButton
        size="medium"
        color={color}
        icon={<Copy />}
        onClick={onCopy}
        isDisabled={disabled}
        ariaLabel={`Copy time range for ${day}`}
      />
    </>
  );
}

function TimeRangeInputs({
  disabled,
  timeRange,
  onStartTimeChange,
  onEndTimeChange,
}: {
  disabled: boolean;
  timeRange: TimeRange;
  onStartTimeChange: (value: string) => void;
  onEndTimeChange: (value: string) => void;
}) {
  return (
    <SDSFlexbox justifyContent="space-between" alignItems="center" gap="text-medium">
      <div className="DayTimeRangeSelector__time-button">
        <TimeSelector
          value={timeRange.start}
          onTimeChange={onStartTimeChange}
          disabled={disabled}
          dataTestId={DataTestIds.ONBOARDING.TIME_FROM}
        />
      </div>
      <SDSText type="button-medium" text="-" color="muted" />
      <div className="DayTimeRangeSelector__time-button">
        <TimeSelector
          value={timeRange.end}
          onTimeChange={onEndTimeChange}
          minAfter={getMinimumEndTime(timeRange.start)}
          disabled={disabled}
          dataTestId={DataTestIds.ONBOARDING.TIME_TO}
        />
      </div>
    </SDSFlexbox>
  );
}

export function DayTimeRangeSelector({ timeRange, day, index, disabled = false }: DayTimeRangeSelectorProps) {
  const { removeTimeRange, updateTimeRange, copyTimeRange, hasOverlap, addTimeRange } = useAvailability();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const errorMessage = getTimeRangeError(timeRange, hasOverlap(day, index));

  const handleCopyClick = (event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleApplyCopy = (selectedDays: string[]) => {
    copyTimeRange(day, index, selectedDays);
  };

  const handleStartTimeChange = (newStartTime: string) => {
    const minimumEndTime = getMinimumEndTime(newStartTime);
    updateTimeRange(day, index, {
      start: newStartTime,
      end: minimumEndTime && timeRange.end < minimumEndTime ? minimumEndTime : timeRange.end,
    });
  };

  const handleEndTimeChange = (newEndTime: string) => {
    updateTimeRange(day, index, { ...timeRange, end: newEndTime });
  };

  const handleAddTimeRange = () => {
    addTimeRange(day);
  };

  const handleRemoveClick = () => {
    setShowRemoveConfirmation(true);
  };

  const handleConfirmRemove = () => {
    removeTimeRange(day, index);
    setShowRemoveConfirmation(false);
  };

  const handleCancelRemove = () => {
    setShowRemoveConfirmation(false);
  };

  return (
    <SDSFlexbox direction="column" gap="button-medium">
      <SDSFlexbox direction="column" gap="text-small" alignItems="flex-start">
        <SDSFlexbox
          alignItems="center"
          className={clsx('DayTimeRangeSelector', {
            'DayTimeRangeSelector--has-error': errorMessage,
          })}
        >
          <TimeRangeInputs
            disabled={disabled}
            timeRange={timeRange}
            onStartTimeChange={handleStartTimeChange}
            onEndTimeChange={handleEndTimeChange}
          />
          <SDSFlexbox gap="button-x-large" alignItems="center" className="DayTimeRangeSelector__actions">
            <TimeRangeActions
              day={day}
              disabled={disabled}
              index={index}
              timeRange={timeRange}
              onAdd={handleAddTimeRange}
              onCopy={handleCopyClick}
              onRemove={handleRemoveClick}
            />
          </SDSFlexbox>
        </SDSFlexbox>
        {errorMessage && (
          <SDSText color="warning" className="DayTimeRangeSelector__warning" type="body-medium" text={errorMessage} />
        )}
      </SDSFlexbox>
      <CopyDaysPopover anchorEl={anchorEl} onClose={handleClosePopover} onApply={handleApplyCopy} day={day} />

      <ConfirmationModal
        isOpen={showRemoveConfirmation}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        description="Deleting a schedule will remove it from all event types. This action cannot be undone."
        buttonText="Delete"
        title="Do you want to delete schedule?"
      />
    </SDSFlexbox>
  );
}
